import React, { useEffect, useState, useContext, Fragment } from 'react';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import ScorecardCreationViewModel, { ScorecardCreationQuestionViewModel, ScorecardCreationSectionViewModel } from './Viewmodels/ScorecardCreationViewModel';
import { APIContext } from '../Contexts/APIContext';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import AddEditScorecardSectionModal from './AddEditScorecardSectionModal';
import AddEditScorecardQuestionModal from './AddEditScorecardQuestionModal';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Divider from '@mui/material/Divider';

type Props = {
    id: number;
}

export default function ScorecardCreationContainer({ id }: Props) {
    const { get } = useContext(APIContext);
    const [loading, setLoading] = useState(false);
    const [scorecard, setScorecard] = useState<ScorecardCreationViewModel>(new ScorecardCreationViewModel());
    const [openAddSection, setOpenAddSection] = useState(false);
    const [editSectionId, setEditSectionId] = useState(0);
    const [openAddQuestion, setOpenAddQuestion] = useState(false);
    const [editQuestionId, setEditQuestionId] = useState(0);
    const [saving, setSaving] = useState(false);
    const [questionSectionId, setQuestionSectionId] = useState(0);

    useEffect(() => {
        if (id !== 0) getData();
    }, [id]);

    const getData = () => {
        setLoading(true);
        get('ScorecardManagement', `GetScorecardContent?templateId=${id}`, (data) => {
            setScorecard(data);
            setLoading(false);
        });
    }

    const toggleOpenAddSection = (id?: number) => {
        if (id) setEditSectionId(id);
        setOpenAddSection((prev) => !prev);
    }

    const closeAddSection = (refresh: boolean) => {
        if (refresh) getData();
        setOpenAddSection(false);
        setEditSectionId(0);
    }

    const toggleOpenAddQuestion = (sectionId: number, id?: number) => {
        if (id) setEditQuestionId(id);
        setQuestionSectionId(sectionId);
        setOpenAddQuestion((prev) => !prev);
    }

    const closeAddQuestion = (refresh: boolean) => {
        if (refresh) getData();
        setOpenAddQuestion(false);
        setEditQuestionId(0);
    }

    const orderSection = (id: number, down: boolean) => {
        setSaving(true);
        get('ScorecardManagement', `ChangeTemplateSectionOrdering?sectionId=${id}&down=${down}`, (data) => {
            setSaving(false);
            getData();
        });
    }

    const orderQuestion = (id: number, down: boolean) => {
        setSaving(true);
        get('ScorecardManagement', `ChangeTemplateQuestionOrdering?questionId=${id}&down=${down}`, (data) => {
            setSaving(false);
            getData();
        });
    }

    return (loading ?
        <CircularProgress size={50} />
        :
        <Grid container spacing={1} sx={{ paddingTop: '4px' }} justifyContent="center" alignItems="center">
            <Grid size={12}>
                <Paper>
                    <Grid container spacing={1} justifyContent="center" alignItems="center">
                        {scorecard && scorecard.sections && scorecard.sections.length > 0 &&
                            <Grid size={12}>
                                <List>
                                    {scorecard.sections.map((section: ScorecardCreationSectionViewModel, index: number) => {
                                        return (
                                            <Fragment>
                                                <ListItem key={index} secondaryAction={
                                                    <Grid container spacing={1} alignItems="center">
                                                        <Grid>
                                                            <IconButton disabled={saving || index === 0} onClick={() => orderSection(section.id, false)} size="small">
                                                                <ArrowUpwardIcon fontSize="small" />
                                                            </IconButton>
                                                        </Grid>
                                                        <Grid>
                                                            <IconButton disabled={saving || index === scorecard.sections.length - 1} onClick={() => orderSection(section.id, true)} size="small">
                                                                <ArrowDownwardIcon fontSize="small" />
                                                            </IconButton>
                                                        </Grid>
                                                        <Grid>
                                                            <IconButton disabled={saving} size="small">
                                                                <EditIcon onClick={() => toggleOpenAddSection(section.id)} fontSize="small" />
                                                            </IconButton>
                                                        </Grid>
                                                    </Grid>
                                                }>
                                                    <ListItemText primary={section.sectionTitle} />
                                                </ListItem>
                                                <List component="div" disablePadding sx={{ pl: 4 }}>
                                                    {section.questions.length > 0 && section.questions.map((question: ScorecardCreationQuestionViewModel, index: number) => {
                                                        return (
                                                            <ListItem key={index} secondaryAction={
                                                                <Grid container spacing={1} alignItems="center">
                                                                    <Grid>
                                                                        <IconButton disabled={saving || index === 0} onClick={() => orderQuestion(question.id, false)} size="small">
                                                                            <ArrowUpwardIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Grid>
                                                                    <Grid>
                                                                        <IconButton disabled={saving || index === section.questions.length - 1} onClick={() => orderQuestion(question.id, true)} size="small">
                                                                            <ArrowDownwardIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Grid>
                                                                    <Grid>
                                                                        <IconButton disabled={saving} onClick={() => toggleOpenAddQuestion(section.id, question.id)} size="small">
                                                                            <EditIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Grid>
                                                                </Grid>
                                                            }>
                                                                <ListItemText primary={question.questionText} secondary={"Category: " + question.category + ", Subcategory: " + question.subCategory + ", Client Category: " + question.clientCategory} />
                                                            </ListItem>
                                                        );
                                                    })}
                                                    <ListItem>
                                                        <Button disabled={saving} fullWidth startIcon={<AddCircleOutlineIcon />} color="primary" size="small" onClick={() => toggleOpenAddQuestion(section.id)}>Add New Question</Button>
                                                    </ListItem>
                                                </List>
                                            </Fragment>
                                        );
                                    })}
                                </List>
                            </Grid>
                        }
                        <Grid size={12}>
                            <Divider />
                        </Grid>
                        <Grid>
                            <Button disabled={saving} fullWidth startIcon={<AddCircleOutlineIcon />} size="small" color="primary" onClick={() => toggleOpenAddSection()}>Add New Section</Button>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
            <AddEditScorecardSectionModal open={openAddSection} editId={editSectionId} onClose={closeAddSection} templateId={id} />
            <AddEditScorecardQuestionModal open={openAddQuestion} editId={editQuestionId} onClose={closeAddQuestion} sectionId={questionSectionId} />
        </Grid>
    );
}